// ✅ Static metadata export (used in project listing page)
export const metadata = {
  title: 'High-End Bathroom Remodeling in Pacific Palisades, CA 90272',
  description:
    'A high-end bathroom remodeling project in Pacific Palisades featuring luxury finishes, custom vanities, premium tile work, and spa-inspired design by Modern Build.',
  link: '/services/bathroom-remodeling',
  height: 'h-[467px]',
  image:
    '/projects/high-end-bathroom-remodeling-in-pacific-palisades-90272/high-end-bathroom-remodeling-in-pacific-palisades-90272.webp',
};

import fs from 'fs';
import path from 'path';
import Head from 'next/head';
import Header from '../../../components/Header';
import Hero from '../../../components/Hero';
import ContentBlock from '../../../components/ContentBlock';
import { VideoSection } from '../../../components/Videos';
import Gallery from '../../../components/Gallery';
import SocialProof from '../../../components/SocialProof';
import ContactUs from '../../../components/ContactUs';
import Footer from '../../../components/Footer';
import { generateWebPageSchema } from '../../../utils/generateWebPageSchema';

export async function getStaticProps() {
  const slug =
    'high-end-bathroom-remodeling-in-pacific-palisades-90272';

  const galleryPath = path.join(
    process.cwd(),
    'public',
    'projects',
    slug,
    'gallery'
  );

  let galleryImages = [];

  // ✅ Load gallery images
  if (fs.existsSync(galleryPath)) {
    const files = fs.readdirSync(galleryPath).filter(file =>
      /\.(jpg|jpeg|png|webp)$/i.test(file)
    );

    galleryImages = files.map(file => ({
      src: `/projects/${slug}/gallery/${file}`,
      alt: file
        .replace(/\.(jpg|jpeg|png|webp)$/i, '')
        .replace(/[-_]/g, ' '),
      title: file
        .replace(/\.(jpg|jpeg|png|webp)$/i, '')
        .replace(/[-_]/g, ' '),
    }));
  }

  return {
    props: {
      galleryImages,
    },
  };
}

export default function HighEndBathroomPacificPalisades({
  galleryImages,
}) {
  const meta = metadata;

  return (
    <>
      <Head>
        <title>{meta.title}</title>
        <meta name="title" content={meta.title} />
        <meta name="description" content={meta.description} />
        <meta name="author" content="Modern Build Home Remodeling" />

        <meta
          name="keywords"
          content="high end bathroom remodeling Pacific Palisades, luxury bathroom remodel 90272, spa bathroom renovation Los Angeles, Modern Build"
        />

        <link
          rel="canonical"
          href="https://modernbuildremodel.com/projects/high-end-bathroom-remodeling-in-pacific-palisades-90272"
        />

        {/* Open Graph */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta
          property="og:image"
          content={`https://modernbuildremodel.com${meta.image}`}
        />
        <meta
          property="og:url"
          content="https://modernbuildremodel.com/projects/high-end-bathroom-remodeling-in-pacific-palisades-90272"
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:site_name"
          content="Modern Build Home Remodeling"
        />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={meta.title} />
        <meta
          name="twitter:description"
          content={meta.description} />
        <meta
          name="twitter:image"
          content={`https://modernbuildremodel.com${meta.image}`} />

        {/* Breadcrumb Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: 'Home',
                  item: 'https://modernbuildremodel.com',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: 'Projects',
                  item: 'https://modernbuildremodel.com/projects',
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name:
                    'High-End Bathroom Remodeling in Pacific Palisades CA 90272',
                  item:
                    'https://modernbuildremodel.com/projects/high-end-bathroom-remodeling-in-pacific-palisades-90272',
                },
              ],
            }),
          }}
        />

        {/* WebPage Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title:
                  'High-End Bathroom Remodeling in Pacific Palisades, CA 90272 | Modern Build',
                description:
                  'Explore a luxury bathroom remodel in Pacific Palisades featuring spa inspired finishes, custom vanities, and premium materials.',
                slug:
                  '/projects/high-end-bathroom-remodeling-in-pacific-palisades-90272',
              })
            ),
          }}
        />
      </Head>

      <Header />

      <Hero
        category="highEndBathroomPacificPalisades90272"
        page="page"
        hasContentH1={false}
      />

      <ContentBlock
        subtitle="PROJECT OVERVIEW"
        title="High-End Bathroom Remodeling in Pacific Palisades"
        description="This luxury bathroom remodeling project in Pacific Palisades transformed an outdated space into a spa inspired retreat. The renovation focused on comfort, elegance, and premium craftsmanship."
        extraText="Every element was carefully selected to enhance daily routines while creating a relaxing, hotel style experience."
        buttonText="Plan My Bathroom Remodel"
        buttonLink="/services/bathroom-remodeling"
      />

      <Gallery images={galleryImages} />

      <section className="bg-white py-[120px]">
        <div className="container mx-auto px-6">
          <VideoSection
            videoUrl="https://www.youtube.com/embed/hyG-KF1zWJI"
            serviceType="Bathroom Remodeling"
            location="Pacific Palisades, CA"
            description="Watch how this Pacific Palisades bathroom was transformed into a luxury spa inspired space with custom finishes and premium details."
            serviceLink="/services/bathroom-remodeling"
            coverImage={meta.image}
            isVideoLeft={true}
          />
        </div>
      </section>

      <section className="bg-white py-[120px]">
  <div className="container mx-auto px-6 max-w-4xl">

    <h2 className="text-2xl font-light text-primary leading-tight font-gilda mb-4">
      High-End Bathroom Remodeling in Pacific Palisades, CA 90272
    </h2>

    <p className="text-gray font-light text-lg leading-relaxed">
      This high-end bathroom remodel in Pacific Palisades transformed an ordinary space into a spa-inspired retreat. Every design choice was focused on elegance, comfort, and lasting functionality.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      Our team balanced premium materials, efficient layout, and attention to detail to create a bathroom that is both practical and luxurious. For related projects, explore our <a
        href="https://modernbuildremodel.com/bathroom-remodeling/california/pacific-palisades/90272"
        className="text-primary underline"
      >
        Bathroom Remodeling
      </a> or <a
        href="https://modernbuildremodel.com/complete-home-remodeling/california/pacific-palisades/90272"
        className="text-primary underline"
      >
        Complete Home Remodeling
      </a> services.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Project Overview
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      The original bathroom had outdated fixtures, limited storage, and insufficient lighting. Our redesign focused on spa-inspired features and high-end finishes to create an open, inviting space.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Design Strategy
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      Custom vanities, premium plumbing fixtures, large-format tiles, and frameless glass shower enclosures were incorporated to achieve a clean, seamless appearance.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      Neutral colors and natural textures enhance the relaxing ambiance and timeless appeal.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Craftsmanship and Execution
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      Every detail, from waterproofing to tile alignment and finishing touches, was executed with precision. This ensures long-term performance and aesthetic integrity.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Results and Homeowner Experience
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      The renovated bathroom now functions as a private spa. Improved storage, lighting, and luxurious finishes enhance daily routines and provide a serene retreat.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      This remodel also increases the home’s long-term value and visual appeal.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Serving Pacific Palisades and Surrounding Areas
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      Modern Build proudly serves Pacific Palisades and nearby Los Angeles communities with high-end remodeling services. Whether you’re planning a full home renovation or a luxury bathroom upgrade, our team delivers exceptional results tailored to your lifestyle.
    </p>

  </div>
</section>


      <SocialProof />
      <ContactUs />
      <Footer />
    </>
  );
}
